import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/pro-regular-svg-icons';
import { EditorImageSkeleton } from '../../canvas/EditorImageSkeleton';
import { blockAlignShell } from '../block-align-shell';
import { resolveExportImageAlt } from '../../domain/image-alt';
import { isFacilityLogoMediaRef, resolveMediaRef } from '../../domain/media';
import { resolveThemeFontFamily } from '../../domain/text-theme';
import {
  IMAGE_LAYOUT_CSS,
  defaultImageHeightPx,
  imageBackgroundTableClass,
  imageOuterTableClass,
  isImageFillColumn,
} from '../../domain/image-layout';
import {
  autoImageReservedWrapperStyle,
  computeAutoImageReservedBox,
  resolveImageIntrinsicSize,
  type AutoImageReservedBox,
} from '../../domain/image-intrinsic';
import {
  imageBlockShellWidth,
  imageBlockWidthCssParts,
} from '../../domain/image-width';
import {
  backgroundImageLayers,
  clipRadiusOverflowStyle,
  imageOverlayRgba,
} from '../../domain/image-overlay';
import type { FacilityProfile } from '../../domain/facility';
import type { ImageBlock } from '../../domain/types';
import {
  padStyle,
  presentationTableProps,
  radiusCss,
} from '../table';
import {
  EDITOR_IMG_PROPS,
  linkPropsForMode,
} from './editor-props';
import { merge } from './merge';
import type { BlockRenderProps } from './types';

function editorImageBoxWidthCss(
  block: ImageBlock,
  contentMaxWidth: number | undefined,
  editorMode: boolean,
): string {
  if (contentMaxWidth == null || contentMaxWidth <= 0) {
    return imageBlockShellWidth(block, 0, editorMode);
  }
  return imageBlockShellWidth(block, contentMaxWidth, editorMode);
}

function resolveFacilityLogoFallbackName(
  alt: string,
  facility: FacilityProfile,
): string {
  const trimmed = alt.trim();
  if (trimmed.length === 0 || trimmed === '{{facility.long_name}}') {
    return facility.name || 'Facility';
  }

  return trimmed;
}

function facilityLogoFallbackCell(
  facilityName: string,
  headingFontFamily: string,
  headingColor: string,
): ReactNode {
  return (
    <div
      style={{
        fontFamily: headingFontFamily,
        fontSize: 20,
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: 'normal',
        color: headingColor,
      }}>
      {facilityName}
    </div>
  );
}

function imagePlaceholderShell(
  block: ImageBlock,
  className: string,
  children: ReactNode,
  tdStyle: CSSProperties = {},
  reservedBox?: AutoImageReservedBox,
): ReactNode {
  const reservedStyle =
    reservedBox != null
      ? autoImageReservedWrapperStyle(block, reservedBox)
      : null;

  return (
    <table
      {...presentationTableProps()}
      style={{
        display: 'inline-table',
        width: reservedStyle?.width ?? editorImageBoxWidthCss(block, undefined, false),
        maxWidth: '100%',
        borderCollapse: 'collapse',
        ...(reservedStyle
          ? {
              marginLeft: reservedStyle.marginLeft,
              marginRight: reservedStyle.marginRight,
            }
          : {}),
      }}>
      <tbody>
        <tr>
          <td
            className={className}
            style={{
              minHeight: reservedBox?.minHeightPx ?? 120,
              aspectRatio: reservedBox?.aspectRatio,
              boxSizing: 'border-box',
              borderRadius: radiusCss(block.borderRadius),
              ...tdStyle,
            }}>
            {children}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function placeholderCell(block: ImageBlock): ReactNode {
  return imagePlaceholderShell(
    block,
    IMAGE_LAYOUT_CSS.emptyPlaceholder,
    <>
      <span
        className={`${IMAGE_LAYOUT_CSS.emptyPlaceholder}__icon`}
        aria-hidden="true">
        <FontAwesomeIcon icon={faImage} />
      </span>
      <span className={`${IMAGE_LAYOUT_CSS.emptyPlaceholder}__label`}>
        Choose image
      </span>
    </>,
    {
      color: '#71717a',
      fontSize: 13,
      textAlign: 'center',
      padding: '16px 24px',
    },
  );
}

function loadingPlaceholderCell(
  block: ImageBlock,
  contentMaxWidth?: number,
): ReactNode {
  if (block.heightMode === 'auto') {
    const intrinsicSize = resolveImageIntrinsicSize(block);
    const reservedBox =
      intrinsicSize != null
        ? computeAutoImageReservedBox(block, contentMaxWidth, intrinsicSize)
        : undefined;
    const skeletonHeightPx = reservedBox?.minHeightPx ?? 120;
    const skeletonWidth = reservedBox != null
      ? '100%'
      : editorImageBoxWidthCss(block, contentMaxWidth, true);

    return imagePlaceholderShell(
      block,
      '',
      <EditorImageSkeleton
        width={skeletonWidth}
        height={`${skeletonHeightPx}px`}
      />,
      {},
      reservedBox,
    );
  }

  const isFill = isImageFillColumn(block);
  const heightPx = defaultImageHeightPx(block);

  return (
    <table
      {...presentationTableProps()}
      className={imageBackgroundTableClass(block)}
      style={{
        ...presentationTableProps().style,
        width: editorImageBoxWidthCss(block, contentMaxWidth, true),
        maxWidth: '100%',
        height: isFill ? '100%' : undefined,
        marginLeft: block.align === 'left' ? 0 : 'auto',
        marginRight: block.align === 'right' ? 0 : 'auto',
      }}>
      <tbody style={isFill ? { height: '100%' } : undefined}>
        <tr style={isFill ? { height: '100%' } : undefined}>
          <td
            className={IMAGE_LAYOUT_CSS.bgTd}
            style={{
              width: '100%',
              height: isFill ? '100%' : heightPx,
              minHeight: isFill ? undefined : heightPx,
              boxSizing: 'border-box',
              borderRadius: radiusCss(block.borderRadius),
            }}>
            <EditorImageSkeleton width="100%" height={`${heightPx}px`} />
          </td>
        </tr>
      </tbody>
    </table>
  );
}


function fallbackImage(
  src: string,
  alt: string,
  editorMode: boolean,
  style: CSSProperties,
  isFill = false,
  onBytesLoaded?: () => void,
): ReactNode {
  const absoluteFill = isFill && editorMode;
  return (
    <img
      src={src}
      alt={alt}
      className={IMAGE_LAYOUT_CSS.bgFallback}
      {...(editorMode ? EDITOR_IMG_PROPS : {})}
      onLoad={onBytesLoaded}
      onError={onBytesLoaded}
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        border: 0,
        opacity: 0,
        ...(absoluteFill
          ? {
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }
          : {}),
        ...style,
      }}
    />
  );
}

function wrapWithImageOverlay(
  content: ReactNode,
  overlayRgba: string | undefined,
  radius: string | undefined,
  style: CSSProperties = {},
): ReactNode {
  if (!overlayRgba) {
    return content;
  }

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        maxWidth: '100%',
        borderRadius: radius,
        lineHeight: 0,
        ...clipRadiusOverflowStyle(radius),
        ...style,
      }}>
      {content}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: overlayRgba,
          pointerEvents: 'none',
        }}
      />
    </span>
  );
}

function backgroundImageCell({
  block,
  src,
  alt,
  href,
  editorMode,
  contentMaxWidth,
  editorImageBytesLoaded = true,
  onEditorImageBytesLoaded,
}: {
  block: ImageBlock;
  src: string;
  alt: string;
  href: string;
  editorMode: boolean;
  contentMaxWidth?: number;
  editorImageBytesLoaded?: boolean;
  onEditorImageBytesLoaded?: () => void;
}): ReactNode {
  const isFill = isImageFillColumn(block);
  const heightPx = defaultImageHeightPx(block);
  const radius = radiusCss(block.borderRadius);
  const imageAlt = resolveExportImageAlt(alt, href, editorMode);
  const bytesPending = editorMode && !editorImageBytesLoaded;
  const bgTdStyle: CSSProperties = {
    width: '100%',
    height: isFill ? '100%' : heightPx,
    minHeight: isFill ? undefined : heightPx,
    backgroundImage: bytesPending
      ? undefined
      : backgroundImageLayers(src, block.imageOverlay),
    backgroundSize: block.objectFit,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    borderRadius: radius,
    lineHeight: 0,
    overflow: 'hidden',
    position: 'relative',
  };
  const inner = fallbackImage(
    src,
    imageAlt,
    editorMode,
    { borderRadius: radius },
    isFill,
    editorMode ? onEditorImageBytesLoaded : undefined,
  );
  const linkProps = href ? linkPropsForMode(editorMode, href) : null;
  const linked =
    linkProps && !editorMode ? (
      <a
        {...linkProps}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          textDecoration: 'none',
        }}>
        {inner}
      </a>
    ) : (
      inner
    );

  return (
    <table
      {...presentationTableProps()}
      className={imageBackgroundTableClass(block)}
      style={{
        ...presentationTableProps().style,
        width: editorImageBoxWidthCss(block, contentMaxWidth, editorMode),
        maxWidth: '100%',
        height: isFill ? '100%' : undefined,
        marginLeft: block.align === 'left' ? 0 : 'auto',
        marginRight: block.align === 'right' ? 0 : 'auto',
      }}>
      <tbody style={isFill ? { height: '100%' } : undefined}>
        <tr style={isFill ? { height: '100%' } : undefined}>
          <td className={IMAGE_LAYOUT_CSS.bgTd} style={bgTdStyle}>
            {bytesPending ? (
              <EditorImageSkeleton width="100%" height={`${heightPx}px`} />
            ) : null}
            {linked}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function autoImageCell({
  block,
  src,
  alt,
  href,
  editorMode,
  contentMaxWidth,
  editorImageBytesLoaded = true,
  onEditorImageBytesLoaded,
}: {
  block: ImageBlock;
  src: string;
  alt: string;
  href: string;
  editorMode: boolean;
  contentMaxWidth?: number;
  editorImageBytesLoaded?: boolean;
  onEditorImageBytesLoaded?: () => void;
}): ReactNode {
  const imageAlt = resolveExportImageAlt(alt, href, editorMode);
  const intrinsicSize = resolveImageIntrinsicSize(block);
  const reservedBox =
    intrinsicSize != null
      ? computeAutoImageReservedBox(block, contentMaxWidth, intrinsicSize)
      : undefined;
  const reservedStyle =
    reservedBox != null
      ? autoImageReservedWrapperStyle(block, reservedBox)
      : null;
  const alignMargins = {
    marginLeft: block.align === 'left' ? 0 : 'auto',
    marginRight: block.align === 'right' ? 0 : 'auto',
  } as const;
  const bytesPending = editorMode && !editorImageBytesLoaded;
  const overlayRgba = imageOverlayRgba(block.imageOverlay);
  const pendingSkeletonHeightPx = reservedBox?.minHeightPx ?? 120;
  const pendingSkeletonWidth =
    reservedBox != null
      ? '100%'
      : editorImageBoxWidthCss(block, contentMaxWidth, editorMode);
  const imgWidthParts = imageBlockWidthCssParts(
    block,
    contentMaxWidth ?? 0,
    editorMode,
  );
  const imgStyle: CSSProperties = {
    width: imgWidthParts.width,
    maxWidth: imgWidthParts.maxWidth,
    height: 'auto',
    borderRadius: overlayRgba ? undefined : radiusCss(block.borderRadius),
    display: 'block',
    border: 0,
    ...(overlayRgba ? {} : alignMargins),
    ...(bytesPending ? { opacity: 0 } : {}),
  };
  const img = (
    <img
      src={src}
      alt={imageAlt}
      {...(intrinsicSize && !editorMode
        ? { width: intrinsicSize.width, height: intrinsicSize.height }
        : {})}
      {...(editorMode ? EDITOR_IMG_PROPS : {})}
      onLoad={editorMode ? onEditorImageBytesLoaded : undefined}
      onError={editorMode ? onEditorImageBytesLoaded : undefined}
      style={imgStyle}
    />
  );
  const linkProps = href ? linkPropsForMode(editorMode, href) : null;
  const linked =
    linkProps && !editorMode ? <a {...linkProps}>{img}</a> : img;
  const linkedWithOverlay = wrapWithImageOverlay(
    linked,
    overlayRgba,
    radiusCss(block.borderRadius),
    alignMargins,
  );
  const shellWidth = reservedStyle?.width ?? editorImageBoxWidthCss(block, contentMaxWidth, editorMode);
  const tdStyle: CSSProperties = {
    textAlign: block.align,
    lineHeight: 0,
    boxSizing: 'border-box',
    borderRadius: radiusCss(block.borderRadius),
    ...(reservedBox
      ? {
          minHeight: reservedBox.minHeightPx,
          aspectRatio: reservedBox.aspectRatio,
          width: reservedStyle?.width,
          maxWidth: reservedStyle?.maxWidth ?? '100%',
          marginLeft: reservedStyle?.marginLeft ?? alignMargins.marginLeft,
          marginRight: reservedStyle?.marginRight ?? alignMargins.marginRight,
        }
      : bytesPending
        ? {
            width: pendingSkeletonWidth,
            maxWidth: '100%',
            minHeight: pendingSkeletonHeightPx,
            marginLeft: alignMargins.marginLeft,
            marginRight: alignMargins.marginRight,
          }
        : {}),
  };

  return (
    <table
      {...presentationTableProps()}
      style={{
        display: 'inline-table',
        width: shellWidth,
        maxWidth: '100%',
        borderCollapse: 'collapse',
        ...(reservedStyle
          ? {
              marginLeft: reservedStyle.marginLeft,
              marginRight: reservedStyle.marginRight,
            }
          : !reservedStyle
            ? alignMargins
            : {}),
      }}>
      <tbody>
        <tr>
          <td align={block.align} style={tdStyle}>
            {bytesPending ? (
              <EditorImageSkeleton
                width={pendingSkeletonWidth}
                height={`${pendingSkeletonHeightPx}px`}
              />
            ) : null}
            {linkedWithOverlay}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export function renderImageBlock({
  block,
  facility,
  textTheme,
  mergeMode,
  editorMode = false,
  editorSuppressSrc = false,
  editorImageBytesLoaded = true,
  onEditorImageBytesLoaded,
  contentMaxWidth,
}: BlockRenderProps & { block: ImageBlock }) {
  const resolvedSrc = resolveMediaRef(block.media, facility);
  const src = editorSuppressSrc ? '' : resolvedSrc;
  const alt = merge(block.alt, mergeMode);
  const href = merge(block.href, mergeMode) || '';
  const usesFacilityLogo = isFacilityLogoMediaRef(block.media);

  const outerStyle: CSSProperties = {
    ...padStyle(block.padding),
    textAlign: block.align,
    lineHeight: 0,
  };

  const isFill = isImageFillColumn(block);

  if (!src) {
    if (usesFacilityLogo) {
      const facilityName = resolveFacilityLogoFallbackName(alt, facility);
      const fallback = facilityLogoFallbackCell(
        facilityName,
        resolveThemeFontFamily(textTheme, 'h1'),
        textTheme.headingColor,
      );
      return blockAlignShell(block.align, fallback, outerStyle);
    }

    const exportSpacer = blockAlignShell(
      block.align,
      <span style={{ fontSize: 0, lineHeight: 0 }}>&nbsp;</span>,
      outerStyle,
    );

    if (block.media === null) {
      return editorMode
        ? blockAlignShell(block.align, placeholderCell(block), outerStyle)
        : exportSpacer;
    }

    const loadingInner = loadingPlaceholderCell(block, contentMaxWidth);

    if (editorMode) {
      if (isFill || block.heightMode === 'fixed') {
        return (
          <table
            {...presentationTableProps()}
            className={imageOuterTableClass(block)}
            style={{
              ...presentationTableProps().style,
              height: isFill ? '100%' : undefined,
            }}>
            <tbody style={isFill ? { height: '100%' } : undefined}>
              <tr style={isFill ? { height: '100%' } : undefined}>
                <td
                  style={{
                    ...outerStyle,
                    ...(isFill ? { height: '100%' } : {}),
                  }}>
                  {loadingInner}
                </td>
              </tr>
            </tbody>
          </table>
        );
      }

      return blockAlignShell(block.align, loadingInner, outerStyle);
    }

    return exportSpacer;
  }

  const inner =
    block.heightMode === 'auto'
      ? autoImageCell({
          block,
          src,
          alt,
          href,
          editorMode,
          contentMaxWidth,
          editorImageBytesLoaded,
          onEditorImageBytesLoaded,
        })
      : backgroundImageCell({
          block,
          src,
          alt,
          href,
          editorMode,
          contentMaxWidth,
          editorImageBytesLoaded,
          onEditorImageBytesLoaded,
        });

  return (
    <table
      {...presentationTableProps()}
      className={imageOuterTableClass(block)}
      style={{
        ...presentationTableProps().style,
        height: isFill ? '100%' : undefined,
      }}>
      <tbody style={isFill ? { height: '100%' } : undefined}>
        <tr style={isFill ? { height: '100%' } : undefined}>
          <td style={{ ...outerStyle, ...(isFill ? { height: '100%' } : {}) }}>
            {inner}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
